import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import { RootState } from "../../../../globals";
import HttpManager from "../../../../services/HttpManager";
import PhoneField from "../../../../components/phoneField";
import { AvailableScheduleDTO, RequestStudentData } from "../../../../models/EnrollmentHistoryDTO";
import DatosCliente, { datosIniciales, datosParaEnviar } from "./datosCliente";
import { PhoneCheckDTO, PrerequestDTO } from "../../../../services/v2/enrollmentPrerequests.service";
import { cursoCorto } from "../../../../utils/cursoCorto";
import { sinTalleresNiEventos } from "../../../../components/enrollment/cursosNoMatriculables";
import SchedulePicker from "../../../../components/enrollment/schedulePicker";
import { telefonoIncompleto } from "../../../../utils/mismoTelefono";
import { formatLocalDateTime } from "../../../../components/enrollment/format";
import { abrirArchivo } from "../../../../components/enrollment/visorArchivo";
import { ordenarHorarios, seLePuedeMatricular } from "../../../academy/enrollStudentModal";
import { errorText } from "../../managment/enrollmentRequests/helpers";
import "./preSolicitudes.css";

interface Comprobante { key: number; file: File; preview: string | null; }
/** Un cliente: lo mínimo (teléfono) y, si la asesora quiere, cursos y horarios, sus datos e identificación. */
interface Persona {
	key: number; phone: string; name: string; scheduleIds: number[]; filtro: string;
	abierto: boolean; datos: RequestStudentData | null; ids: Comprobante[];
}

export interface PreSolicitudFormProps {
	campusId: number;
	countryId: number;
	onClose: () => void;
	/** Al enviar: para refrescar la bandeja de atrás. */
	onSent: (pre: PrerequestDTO) => void;
	/** Desde "Inscribir" en la ficha del CRM: teléfono, nombre y el curso que le interesa (filtra los horarios). */
	inicial?: { phone: string; name: string; curso: string } | null;
	/** El plantel confirmado antes de abrir (se enseña en el título). */
	plantelNombre?: string;
}

const MAX_BYTES = 15 * 1024 * 1024;
// Conceptos de cobro que viven como "curso" pero no son una venta (igual que VentasConteo en el servidor).
const NO_ES_MATRICULA = /graduaci|gastos\s+de|paquete\s+de/i;
let siguiente = 1;
const personaVacia = (): Persona => ({ key: siguiente++, phone: "", name: "", scheduleIds: [], filtro: "", abierto: false, datos: null, ids: [] });
const MAX_IDS = 4;

/**
 * Las fotos del celular pesan 3-6 MB: se achican a 1800 px en JPEG antes de subirlas (igual que en
 * "Reportar mi pago"). Si algo falla se sube el archivo tal cual.
 */
const achicar = (file: File): Promise<File> => new Promise((resolve) => {
	if (!file.type.startsWith("image/") || file.type === "image/gif") {
		resolve(file);
		return;
	}
	const url = URL.createObjectURL(file);
	const img = new Image();
	img.onload = () => {
		const escala = Math.min(1, 1800 / Math.max(img.width, img.height));
		if (escala === 1 && file.size < 1.5 * 1024 * 1024 && file.type !== "image/heic") {
			URL.revokeObjectURL(url);
			resolve(file);
			return;
		}
		const canvas = document.createElement("canvas");
		canvas.width = Math.round(img.width * escala);
		canvas.height = Math.round(img.height * escala);
		const ctx = canvas.getContext("2d");
		if (!ctx) { URL.revokeObjectURL(url); resolve(file); return; }
		ctx.fillStyle = "#fff";
		ctx.fillRect(0, 0, canvas.width, canvas.height);
		ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
		canvas.toBlob((blob) => {
			URL.revokeObjectURL(url);
			resolve(blob ? new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" }) : file);
		}, "image/jpeg", 0.85);
	};
	img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
	img.src = url;
});

/**
 * Solicitud de la asesora (antes "pre-solicitud"): en 30 segundos desde el celular. Uno o más clientes (teléfono,
 * nombre opcional y, si quiere, cursos y horarios con el selector de Matrículas), al menos un comprobante y una nota.
 * Los datos completos los pone después la supervisora (Esteban, 2026-10-03 y 2026-10-05).
 */
const PreSolicitudForm: React.FC<PreSolicitudFormProps> = ({ campusId, countryId, onClose, onSent, inicial, plantelNombre }) => {
	const http = HttpManager.getInstance();
	const basicInfo: any = useSelector((state: RootState) => state.basicInfo.value);
	const [horarios, setHorarios] = useState<AvailableScheduleDTO[] | null>(null);
	const [personas, setPersonas] = useState<Persona[]>(() => [inicial ? { ...personaVacia(), phone: inicial.phone.replace(/\D/g, ""), name: inicial.name, filtro: inicial.curso } : personaVacia()]);
	const [comprobantes, setComprobantes] = useState<Comprobante[]>([]);
	const [nota, setNota] = useState<string>("");
	const [intentado, setIntentado] = useState<boolean>(false);
	const [enviando, setEnviando] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [listo, setListo] = useState<PrerequestDTO | null>(null);
	const [repetidos, setRepetidos] = useState<Record<number, PhoneCheckDTO[]>>({});
	const archivo = useRef<HTMLInputElement | null>(null);
	const timers = useRef<Record<number, any>>({});

	useEffect(() => {
		http.enrollmentPrerequestsService.schedules(campusId)
			.then((r) => sinTalleresNiEventos(campusId, r.data || []))
			.then((lista) => setHorarios(ordenarHorarios(lista.filter((h) => seLePuedeMatricular(h) && !NO_ES_MATRICULA.test(h.courseName || "")))))
			.catch((e) => { setHorarios([]); setError(errorText(e)); });
	}, [campusId]);

	// No se deja la página de atrás moviéndose debajo del formulario.
	useEffect(() => {
		const antes = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => { document.body.style.overflow = antes; };
	}, []);

	useEffect(() => () => comprobantes.forEach((c) => c.preview && URL.revokeObjectURL(c.preview)), []);

	const cambiar = (key: number, cambios: Partial<Persona>) => setPersonas((lista) => lista.map((p) => p.key === key ? { ...p, ...cambios } : p));

	/* Cursos y horarios: el MISMO selector que Matrículas, varios a la vez y opcionales (Esteban, 2026-10-05). */
	const alternarHorario = (p: Persona, id: number) => cambiar(p.key, {
		scheduleIds: p.scheduleIds.includes(id) ? p.scheduleIds.filter((x) => x !== id) : [...p.scheduleIds, id]
	});
	// Un módulo se matricula en un solo horario: el mismo aviso que Matrículas (y el servidor lo rechaza).
	const modulosRepetidos = (p: Persona): string[] => {
		const porModulo: Record<string, AvailableScheduleDTO[]> = {};
		p.scheduleIds.forEach((id) => {
			const h = (horarios || []).find((x) => x.id === id);
			if (h && h.levelId) (porModulo[h.levelId] = porModulo[h.levelId] || []).push(h);
		});
		return Object.values(porModulo).filter((g) => g.length > 1).map((g) => `${cursoCorto(g[0].courseName)} · ${g[0].levelName}`);
	};

	const alCambiarTelefono = (p: Persona, phone: string) => {
		cambiar(p.key, { phone });
		if (timers.current[p.key]) clearTimeout(timers.current[p.key]);
		const digitos = phone.replace(/\D/g, "");
		if (digitos.length < 10 || telefonoIncompleto(phone)) {
			setRepetidos((r) => ({ ...r, [p.key]: [] }));
			return;
		}
		timers.current[p.key] = setTimeout(() => {
			http.enrollmentPrerequestsService.phoneCheck(campusId, digitos)
				.then((r) => setRepetidos((x) => ({ ...x, [p.key]: r.data || [] })))
				.catch(() => { /* el aviso es ayuda, no estorba si falla */ });
		}, 500);
	};

	/** Fotos achicadas y con miniatura, listas para mostrar y subir. */
	const preparar = async (files: File[]): Promise<Comprobante[]> => {
		setError(null);
		const nuevos: Comprobante[] = [];
		for (const original of files) {
			const file = await achicar(original);
			if (file.size > MAX_BYTES) {
				setError("Un archivo pasa de 15 MB: toma la foto otra vez o súbelo en PDF.");
				continue;
			}
			const preview = file.type.startsWith("image/") && !/hei[cf]/i.test(file.type) ? URL.createObjectURL(file) : null;
			nuevos.push({ key: siguiente++, file, preview });
		}
		return nuevos;
	};

	const subir = async (lista: FileList | null) => {
		const files = Array.from(lista || []);
		if (archivo.current) archivo.current.value = "";
		if (files.length < 1) return;
		const nuevos = await preparar(files);
		setComprobantes((c) => [...c, ...nuevos]);
	};

	// Identificación del cliente (opcional): frente y reverso.
	const subirId = async (p: Persona, input: HTMLInputElement) => {
		const files = Array.from(input.files || []);
		input.value = "";
		if (files.length < 1) return;
		const nuevos = await preparar(files);
		setPersonas((lista) => lista.map((x) => x.key === p.key ? { ...x, ids: [...x.ids, ...nuevos].slice(0, MAX_IDS) } : x));
	};
	const quitarId = (p: Persona, c: Comprobante) => {
		if (c.preview) URL.revokeObjectURL(c.preview);
		setPersonas((lista) => lista.map((x) => x.key === p.key ? { ...x, ids: x.ids.filter((y) => y.key !== c.key) } : x));
	};

	/** "Llenar más datos": el nombre de una línea pasa a Nombres y Apellidos, y de regreso al cerrar. */
	const alternarDatos = (p: Persona) => {
		if (p.abierto) {
			const nombre = [p.datos?.firstName, p.datos?.lastName].filter((x) => String(x || "").trim() !== "").join(" ");
			cambiar(p.key, { abierto: false, name: nombre || p.name });
			return;
		}
		const tipo = basicInfo?.countryRef?.["C_" + countryId]?.preferred_identification_type_id || "";
		const base = p.datos || datosIniciales(countryId, tipo, "");
		const partido = datosIniciales(countryId, tipo, p.name);
		cambiar(p.key, { abierto: true, datos: { ...base, firstName: p.name.trim() ? partido.firstName : base.firstName, lastName: p.name.trim() ? partido.lastName : base.lastName } });
	};

	const filaArchivo = (c: Comprobante, onQuitar: () => void) => (
		<div key={c.key} className="psComprobante">
			<button type="button" className="psMiniatura" aria-label="Ver archivo"
				onClick={() => abrirArchivo({ origen: "Solicitudes", nombre: c.file.name, urlLista: c.preview, cargar: () => Promise.resolve(c.file) })}>
				{c.preview ? <img src={c.preview} alt="" /> : <FontAwesomeIcon icon={solid("file-pdf")} />}
			</button>
			<span className="psFilaTexto"><span className="psFilaTitulo" style={{ fontSize: 15, wordBreak: "break-all" }}>{c.file.name}</span></span>
			<button type="button" className="psQuitar" onClick={onQuitar} aria-label="Quitar"><FontAwesomeIcon icon={solid("xmark")} /></button>
		</div>
	);

	const quitar = (c: Comprobante) => {
		if (c.preview) URL.revokeObjectURL(c.preview);
		setComprobantes((lista) => lista.filter((x) => x.key !== c.key));
	};

	const faltaDe = (p: Persona): string[] => {
		const f: string[] = [];
		const d = p.phone.replace(/\D/g, "");
		if (d.length < 10 || telefonoIncompleto(p.phone)) f.push("telefono");
		if (modulosRepetidos(p).length > 0) f.push("modulo");
		return f;
	};
	const algoFalta = personas.some((p) => faltaDe(p).length > 0) || comprobantes.length < 1;

	const enviar = async () => {
		setIntentado(true);
		if (algoFalta || !nota.trim()) {
			setError(comprobantes.length < 1 && !personas.some((p) => faltaDe(p).length > 0) ? "Falta el comprobante." : !algoFalta ? "Falta la nota de matrícula." : "Revisa lo que está marcado.");
			return;
		}
		setEnviando(true);
		setError(null);
		try {
			const r = await http.enrollmentPrerequestsService.create(campusId,
				personas.map((p) => ({
					phone: p.phone.replace(/\D/g, ""), scheduleIds: p.scheduleIds,
					name: p.abierto ? [p.datos?.firstName, p.datos?.lastName].filter(Boolean).join(" ").trim() : p.name.trim(),
					studentData: p.datos ? datosParaEnviar(p.datos) : null
				})),
				nota.trim(), comprobantes.map((c) => c.file), personas.map((p) => p.ids.map((c) => c.file)));
			setListo(r.data);
			onSent(r.data);
		} catch (e) {
			setError(errorText(e));
		} finally {
			setEnviando(false);
		}
	};

	const otra = () => {
		comprobantes.forEach((c) => c.preview && URL.revokeObjectURL(c.preview));
		personas.forEach((p) => p.ids.forEach((c) => c.preview && URL.revokeObjectURL(c.preview)));
		setPersonas([personaVacia()]);
		setComprobantes([]);
		setNota("");
		setIntentado(false);
		setRepetidos({});
		setListo(null);
	};

	const cabeza = (
		<div className="psCabeza">
			<h1>Nueva Solicitud{plantelNombre ? <span style={{ fontWeight: 500, color: "var(--wa-apagado)" }}> · {plantelNombre}</span> : null}</h1>
			<button type="button" className="psCerrar" onClick={onClose} aria-label="Cerrar" disabled={enviando}><FontAwesomeIcon icon={solid("xmark")} /></button>
		</div>
	);

	if (listo) {
		return (
			<div className="ps" role="dialog" aria-modal="true">
				{cabeza}
				<div className="psCuerpo psListo">
					<div className="psPalomita"><FontAwesomeIcon icon={solid("check")} /></div>
					<div className="psTitulo" style={{ marginBottom: 4 }}>Solicitud enviada</div>
					<div className="psFolio">#{listo.id}</div>
					<div style={{ color: "var(--ps-gris)" }}>{listo.people.length === 1 ? "1 cliente" : `${listo.people.length} clientes`}</div>
					{(listo.notices || []).map((n, i) => (
						<div className="psAviso" key={"n_" + i}><FontAwesomeIcon icon={solid("circle-info")} /><span>{n}</span></div>
					))}
					<div className="psListoBotones">
						<button type="button" className="psBoton" onClick={otra}><FontAwesomeIcon icon={solid("plus")} />Hacer otra</button>
						<button type="button" className="psBoton secundario" onClick={onClose}>Listo</button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="ps" role="dialog" aria-modal="true">
			{cabeza}
			<div className="psCuerpo">
				{personas.map((p, i) => {
					const falta = intentado ? faltaDe(p) : [];
					const incompleto = telefonoIncompleto(p.phone);
					return (
						<div key={p.key} className={"psTarjeta" + (falta.length > 0 ? " falta" : "")}>
							<div className="psTarjetaCabeza">
								<b>Cliente #{i + 1}</b>
								{personas.length > 1 ? <button type="button" className="psQuitarTexto" onClick={() => setPersonas((l) => l.filter((x) => x.key !== p.key))}>Quitar</button> : null}
							</div>
							<label className="psEtiqueta" htmlFor={"psTel" + p.key}>Teléfono</label>
							<PhoneField id={"psTel" + p.key} name={"psTel" + p.key} value={p.phone} campusCountryId={countryId} required
								onChange={(v: string) => alCambiarTelefono(p, v)} />
							{incompleto ? <div className="psFalta">{incompleto}</div> : (falta.includes("telefono") ? <div className="psFalta">Falta el teléfono</div> : null)}
							{(repetidos[p.key] || []).map((x) => (
								<div className="psAviso" key={"rep_" + x.prerequestId}>
									<FontAwesomeIcon icon={solid("circle-info")} />
									<span>Ya está en la solicitud #{x.prerequestId} de {x.createdBy}, {formatLocalDateTime(x.createdAt)}</span>
								</div>
							))}

							{p.abierto ? null : (
								<>
									<label className="psEtiqueta" htmlFor={"psNom" + p.key}>Nombre <small>opcional</small></label>
									<input id={"psNom" + p.key} className="psInput" type="text" autoComplete="off" value={p.name} onChange={(e) => cambiar(p.key, { name: e.target.value })} />
								</>
							)}

							<div className="psEtiqueta psEtiquetaHorarios">Cursos y horarios <small>opcional</small></div>
							<div className="psHorarios">
								<SchedulePicker schedules={horarios} picked={p.scheduleIds} onToggle={(h) => alternarHorario(p, h.id)}
									filter={p.filtro} onFilter={(v) => cambiar(p.key, { filtro: v })} disabled={enviando} title=" " tarjetas />
							</div>
							{p.scheduleIds.length > 0 ? (
								<div className="psElegidos">{p.scheduleIds.length === 1 ? "1 horario elegido" : `${p.scheduleIds.length} horarios elegidos`}</div>
							) : null}
							{modulosRepetidos(p).length > 0 ? (
								<div className="psFalta">Elegiste dos horarios del mismo módulo ({modulosRepetidos(p).join(", ")}): deja uno.</div>
							) : null}

							<div className="psEtiqueta" style={{ marginTop: 16 }}>Identificación <small>opcional</small></div>
							{p.ids.map((c) => filaArchivo(c, () => quitarId(p, c)))}
							{p.ids.length < MAX_IDS ? (
								<label className="psSubir chico" style={{ minHeight: 52 }}>
									<FontAwesomeIcon icon={p.ids.length > 0 ? solid("plus") : solid("id-card")} />
									<span>{p.ids.length > 0 ? "Agregar reverso u otra" : "Foto o PDF"}</span>
									<input type="file" accept="image/*,application/pdf" multiple hidden onChange={(e) => subirId(p, e.currentTarget)} />
								</label>
							) : null}

							<button type="button" className={"psMasDatos" + (p.abierto ? " abierto" : "")} aria-expanded={p.abierto} onClick={() => alternarDatos(p)}>
								<FontAwesomeIcon icon={p.abierto ? solid("chevron-up") : solid("chevron-down")} />&nbsp; {p.abierto ? "Ocultar datos" : "Llenar más datos"}
							</button>
							{p.abierto && p.datos ? <DatosCliente clave={p.key} countryId={countryId} datos={p.datos} onChange={(d) => cambiar(p.key, { datos: d })} /> : null}
						</div>
					);
				})}
				<button type="button" className="psMas" onClick={() => setPersonas((l) => [...l, personaVacia()])}>
					<FontAwesomeIcon icon={solid("user-plus")} />&nbsp; Agregar otro cliente
				</button>

				<div className="psEtiqueta" style={{ marginTop: 22, fontSize: 17 }}>Comprobantes</div>
				<input ref={archivo} type="file" accept="image/*,application/pdf" multiple hidden onChange={(e) => subir(e.target.files)} />
				{comprobantes.map((c) => filaArchivo(c, () => quitar(c)))}
				<button type="button" className={"psSubir" + (comprobantes.length > 0 ? " chico" : "") + (intentado && comprobantes.length < 1 ? " falta" : "")} onClick={() => archivo.current?.click()}>
					<FontAwesomeIcon icon={comprobantes.length > 0 ? solid("plus") : solid("camera")} />
					<span>{comprobantes.length > 0 ? "Agregar otro" : "Tomar foto o elegir archivo"}</span>
					{comprobantes.length === 0 ? <small>Foto o PDF</small> : null}
				</button>
				{intentado && comprobantes.length < 1 ? <div className="psFalta">Falta el comprobante</div> : null}

				{/*
					Es la nota de matrícula de cada curso cuando el cliente llena sus datos por el formulario de WhatsApp y la Matrícula
					se arma sola (2026-10-19): Admisiones no recibe una sin nota, por eso ya no es opcional.
				*/}
				<label className="psEtiqueta" htmlFor="psNota" style={{ marginTop: 22, fontSize: 17 }}>Nota de Matrícula <small>promoción, descuento y lo que pagó</small></label>
				<textarea id="psNota" className={"psInput" + (intentado && !nota.trim() ? " falta" : "")} rows={3} value={nota} onChange={(e) => setNota(e.target.value)}
					placeholder="Ej. Inscripción gratis + 30 % en la primera mensualidad. Pagó L.1,050 por transferencia." />
				{intentado && !nota.trim() ? <div className="psFalta">Falta la nota de matrícula</div> : null}
			</div>
			<div className="psPie">
				<div className="psPieDentro">
					{error ? <div className="psError">{error}</div> : null}
					<button type="button" className="psBoton" disabled={enviando} onClick={enviar}>
						{enviando ? <><span className="psGiro" />Enviando…</> : <><FontAwesomeIcon icon={solid("paper-plane")} />Enviar</>}
					</button>
				</div>
			</div>
		</div>
	);
};

export default PreSolicitudForm;
